import { useCallback, useEffect, useState } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PauseIcon,
  PlayIcon,
  ShieldCheckIcon,
} from "./icons";
import { scrollToContacto } from "./scroll";

export default function FeatureCarousel({ slides, label, interval = 7000 }) {
  const [current, setCurrent] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [stopped, setStopped] = useState(false);
  const total = slides.length;

  const goTo = useCallback(
    (index) => setCurrent((index + total) % total),
    [total],
  );
  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    if (hovered || stopped || total < 2) return undefined;
    const timer = setTimeout(next, interval);
    return () => clearTimeout(timer);
  }, [current, hovered, stopped, interval, next, total]);

  return (
    <section
      className="kn-feature-carousel"
      aria-roledescription="carrusel"
      aria-label={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="kn-container">
        <div className="kn-carousel-track">
          {slides.map((slide, index) => {
            const active = index === current;
            return (
              <div
                key={slide.title}
                className={`kn-intro-grid kn-fc-slide${active ? " is-active" : ""}`}
                role="group"
                aria-roledescription="diapositiva"
                aria-label={`${index + 1} de ${total}`}
                aria-hidden={!active}
              >
                <div>
                  <span className="kn-eyebrow">{slide.eyebrow}</span>
                  <h1 className="kn-fc-title">{slide.title}</h1>
                  <p className="kn-hero-lead">{slide.lead}</p>
                  <div className="kn-hero-actions">
                    <button
                      type="button"
                      className="kn-btn-primary"
                      tabIndex={active ? 0 : -1}
                      onClick={scrollToContacto}
                    >
                      Solicitar cotización
                    </button>
                    <button
                      type="button"
                      className="kn-btn-secondary"
                      tabIndex={active ? 0 : -1}
                      onClick={scrollToContacto}
                    >
                      Hablar con un asesor
                    </button>
                  </div>
                  <p className="kn-trust">
                    <ShieldCheckIcon />
                    {slide.tag}
                  </p>
                </div>
                <div>
                  <div className="kn-fc-media">
                    <img src={slide.image} alt={slide.imageAlt} />
                  </div>
                  <ul className="kn-fc-features">
                    {slide.features.map(({ icon: Icon, title, text }) => (
                      <li key={title}>
                        <Icon />
                        <div>
                          <h2>{title}</h2>
                          <p>{text}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        <div className="kn-fc-controls">
          <button
            type="button"
            className="kn-fc-btn kn-fc-btn--outlined"
            onClick={prev}
            aria-label="Diapositiva anterior"
          >
            <ChevronLeftIcon />
          </button>
          <div className="kn-fc-dots">
            {slides.map(({ eyebrow }, index) => (
              <button
                key={eyebrow}
                type="button"
                className="kn-fc-btn"
                onClick={() => goTo(index)}
                aria-label={`Ver diapositiva ${index + 1} de ${total}`}
                aria-current={index === current}
                title={eyebrow}
              >
                <span
                  className={`kn-fc-dot${index === current ? " is-active" : ""}`}
                />
              </button>
            ))}
          </div>
          <button
            type="button"
            className="kn-fc-btn kn-fc-btn--outlined"
            onClick={next}
            aria-label="Diapositiva siguiente"
          >
            <ChevronRightIcon />
          </button>
          <button
            type="button"
            className="kn-fc-btn"
            onClick={() => setStopped((value) => !value)}
            aria-label={stopped ? "Reanudar carrusel" : "Pausar carrusel"}
            title={stopped ? "Reanudar carrusel" : "Pausar carrusel"}
          >
            {stopped ? <PlayIcon /> : <PauseIcon />}
          </button>
        </div>
      </div>
    </section>
  );
}
